import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mountain, ArrowRight, ChevronRight, MapPin, CheckCircle2 } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import Seo from "../components/Seo";
import { SITE_URL, BUSINESS, aptUrl } from "../lib/site";
import { SEO_PAGES } from "../data/seoPages";

const RELATED = [
  { slug: "appartamenti-a-dimaro", label: "Appartamenti a Dimaro" },
  { slug: "hotel-a-dimaro-folgarida", label: "Dove dormire a Dimaro" },
  { slug: "dormire-vicino-impianti-folgarida-marilleva", label: "Dormire vicino agli impianti" },
  { slug: "vacanze-val-di-sole-inverno", label: "Val di Sole in inverno" },
  { slug: "cosa-fare-a-dimaro-in-estate", label: "Dimaro in estate" },
  { slug: "appartamenti-per-famiglie-val-di-sole", label: "Appartamenti per famiglie" },
];

export default function SeoLanding({ slug }) {
  const page = SEO_PAGES[slug];
  const [apartments, setApartments] = useState([]);

  useEffect(() => { api.get("/apartments").then((r) => setApartments(r.data)).catch(() => {}); }, []);

  if (!page) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": page.metaTitle,
        "description": page.description,
        "url": `${SITE_URL}/${slug}`,
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL + "/" },
          { "@type": "ListItem", "position": 2, "name": page.h1, "item": `${SITE_URL}/${slug}` },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": page.faq.map((f) => ({
          "@type": "Question", "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  };

  return (
    <div className="bg-ivory min-h-screen">
      <Seo title={page.metaTitle.replace(" | Appartamenti in Val di Sole", "")} description={page.description} path={`/${slug}`} jsonLd={jsonLd} />

      {/* NAV */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 bg-fir text-ivory">
        <Link to="/" className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-xl">Appartamenti in Val di Sole</span></Link>
        <Link to="/#strutture" data-testid="seo-nav-cta" className="gold-btn px-4 py-2 rounded-xl text-sm">Verifica disponibilità</Link>
      </nav>

      {/* BREADCRUMB */}
      <div className="max-w-4xl mx-auto px-6 pt-8 text-sm text-fir-deep/60 flex items-center gap-1">
        <Link to="/" className="hover:text-gold">Home</Link><ChevronRight size={14} /><span className="text-fir-deep/80">{page.h1}</span>
      </div>

      {/* HERO / INTRO */}
      <header className="max-w-4xl mx-auto px-6 pt-4 pb-8">
        <p className="text-wood uppercase tracking-[0.2em] text-sm mb-3">{page.eyebrow}</p>
        <h1 className="font-display text-4xl md:text-5xl text-fir leading-tight mb-5">{page.h1}</h1>
        <p className="text-fir-deep/80 text-lg leading-relaxed">{page.intro}</p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Link to="/#strutture" data-testid="seo-hero-cta" className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2">Verifica disponibilità <ArrowRight size={16} /></Link>
          <a href={`tel:${BUSINESS.phoneHref}`} className="px-6 py-3 rounded-xl border border-fir/30 text-fir hover:bg-fir/5 transition-colors">Chiama {BUSINESS.phone}</a>
        </div>
      </header>

      {/* SECTIONS */}
      <section className="max-w-4xl mx-auto px-6 py-6 space-y-10">
        {page.sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-display text-2xl md:text-3xl text-fir mb-3">{s.h2}</h2>
            <p className="text-fir-deep/75 text-lg leading-relaxed">{s.body}</p>
          </div>
        ))}
      </section>

      {/* PROPERTIES TEASER (internal links) */}
      {apartments.length > 0 && (
        <section className="bg-stone/50 py-14">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="font-display text-3xl text-fir text-center mb-2">Le nostre strutture a Dimaro</h2>
            <p className="text-fir-deep/70 text-center mb-10">Scegli l'alloggio, verifica le date e prenota diretto al miglior prezzo.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {apartments.slice(0, 3).map((a) => (
                <Link key={a.id} to={aptUrl(a)} data-testid="seo-property-link" className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-shadow group">
                  <img src={resolveImg(a.images?.[0])} alt={`${a.title} — Dimaro, Val di Sole`} loading="lazy" className="h-48 w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="p-5">
                    <h3 className="font-display text-xl text-fir">{a.title}</h3>
                    <p className="text-fir-deep/60 text-sm mt-1">{a.subtitle}</p>
                    <span className="inline-flex items-center gap-1 text-gold text-sm mt-3">Scopri di più <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-14">
        <h2 className="font-display text-3xl text-fir mb-6">Domande frequenti</h2>
        <div className="space-y-3">
          {page.faq.map((f, i) => (
            <details key={i} data-testid="seo-faq-item" className="bg-white rounded-2xl border border-stone p-5 group">
              <summary className="font-medium text-fir cursor-pointer list-none flex items-center justify-between">{f.q}<ChevronRight size={18} className="text-gold group-open:rotate-90 transition-transform" /></summary>
              <p className="text-fir-deep/75 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* WHY DIRECT + CTA */}
      <section className="bg-fir text-ivory py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl mb-4">Prenota direttamente da Appartamenti in Val di Sole</h2>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-ivory/85 mb-8">
            {["Miglior prezzo, senza commissioni", "Contatto diretto con il proprietario", "Assistenza prima e durante il soggiorno"].map((x) => (
              <span key={x} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-gold" />{x}</span>
            ))}
          </div>
          <Link to="/#strutture" data-testid="seo-final-cta" className="gold-btn px-8 py-3.5 rounded-xl inline-flex items-center gap-2 text-lg">Verifica disponibilità <ArrowRight size={18} /></Link>
        </div>
      </section>

      {/* RELATED LINKS */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="font-display text-2xl text-fir mb-5">Approfondimenti</h2>
        <div className="flex flex-wrap gap-3">
          {RELATED.filter((r) => r.slug !== slug).map((r) => (
            <Link key={r.slug} to={`/${r.slug}`} className="px-4 py-2 rounded-full bg-white border border-stone text-fir hover:border-gold hover:text-gold transition-colors text-sm">{r.label}</Link>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-fir-deep text-ivory px-6 py-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-2xl">Appartamenti in Val di Sole</span></div>
          <div className="text-ivory/60 text-sm flex items-center gap-2"><MapPin size={15} className="text-gold" /> {BUSINESS.locality}, Val di Sole (TN) · {BUSINESS.phone}</div>
          <span className="text-ivory/40 text-sm">© {new Date().getFullYear()} Appartamenti in Val di Sole</span>
        </div>
        <div className="max-w-6xl mx-auto mt-4 flex gap-4 text-sm">
          <Link to="/guide" className="text-ivory/50 hover:text-gold">Guide</Link>
          <Link to="/privacy" className="text-ivory/50 hover:text-gold">Privacy</Link>
          <Link to="/cookie-policy" className="text-ivory/50 hover:text-gold">Cookie Policy</Link>
        </div>
      </footer>
    </div>
  );
}
